import React, { useState, useEffect, useRef } from 'react';
import GlobalFunction from './globalFunction';
import { Grid, Row, Col, FormGroup, Button, FormControl, Glyphicon, DropdownButton,
         MenuItem, ListGroup, ListGroupItem, Modal } from 'react-bootstrap';
import EventSystem from './EventSystem';
import MyAudioPlayer from './myAudioPlayer';
import _ from 'lodash';
import webRtcController from './webRtcController';
import { prefetchTurnCredentials, getTurnCredentials } from './turnCredentials';
import MiniAnalyser from './miniAnalyser';
import mediaDeviceClass from './mediaDeviceClass';
import keyGen from 'random-key';
import { Tile, Title, Body } from './style/tile.style';
import './css/webcamModule.css';
import './css/audioClient.css';


const FFTSIZE = 1024;

function VerticalAudioMeter({ meterRef, height = 22 }) {
    const meterTrackWidth = 3;

    return (
        <div ref={meterRef} data-meter-height={height}
            style={{ position: 'relative', width: 12, height, flexShrink: 0 }}>
            <div style={{ width: meterTrackWidth, height, backgroundColor: '#333333',
                          position: 'absolute', left: 4, top: 0, zIndex: 2 }} />
            <div style={{ width: meterTrackWidth, height,
                          backgroundImage: 'linear-gradient(red 0%, red 12%, yellow 12%, yellow 37%, gray 37%, gray 100%)',
                          position: 'absolute', left: 4, top: 0, zIndex: 1 }} />
            <div style={{ width: 0, height: 0, borderTop: '2px solid transparent',
                          borderBottom: '2px solid transparent', borderLeft: '2px solid white',
                          position: 'absolute', left: 9, top: height - 2, zIndex: 3 }} />
        </div>
    );
}

function updateVerticalAudioMeter(meterRef, instantVolume, slowVolume) {
    const meter = meterRef.current;
    if (!meter) return;

    const height = Number(meter.dataset.meterHeight) || 22;
    const mask = meter.children[0];
    const marker = meter.children[2];
    if (!mask || !marker) return;

    mask.style.height = `${(1 - instantVolume) * height}px`;
    marker.style.top = `${Math.max(0, Math.min(height - 2, (1 - slowVolume) * height - 1))}px`;
}

function getStoredClientDetailsKey(config) {
    return `${config.module_name}_${config.event_screen_module_id}`;
}

function readStoredClientDetails(config) {
    try {
        return JSON.parse(localStorage.getItem(getStoredClientDetailsKey(config))) || {};
    } catch (e) {
        return {};
    }
}

function writeStoredClientDetails(config, details) {
    const stored = readStoredClientDetails(config);
    localStorage.setItem(getStoredClientDetailsKey(config), JSON.stringify({ ...stored, ...details }));
}

function isIOSBrowser() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export default function AudioClient({ parameters, onSendMessage, mode }) {
    const [moduleWidth, setModuleWidth] = useState(0);
    const [moduleHeight, setModuleHeight] = useState(0);
    const [validationError, setValidationError] = useState(null);
    const [name, setName] = useState('');
    const [myRegisteredName, setMyRegisteredName] = useState('');
    const [registeredNames, setRegisteredNames] = useState([]);
    const [peerToConnectWith, setPeerToConnectWith] = useState('');
    const [maxInitLocalVideoHeight, setMaxInitLocalVideoHeight] = useState(0);
    const [initiator, setInitiator] = useState(false);
    const [callStatus, setCallStatus] = useState('none');
    const [hideRemoteVideo, setHideRemoteVideo] = useState(true);
    const [cameraMuted, setCameraMuted] = useState(false);
    const [micMuted, setMicMuted] = useState(false);
    const [remoteCameraMuted, setRemoteCameraMuted] = useState(false);
    const [remoteAudioMuted, setRemoteAudioMuted] = useState(false);
    const [handRaised, setHandRaised] = useState(false);
    const [componentMounted, setComponentMounted] = useState(false);
    const [fileManager, setFileManager] = useState(false);
    const [gracefulCallTermination, setGracefulCallTermination] = useState(false);
    const [initiatedHangUp, setInitiatedHangUp] = useState(false);
    const [terminationMessage, setTerminationMessage] = useState('');
    const [fileSelectedName, setFileSelectedName] = useState('');
    const [audioListItems, setAudioListItems] = useState([]);
    const [browserSupportFails, setBrowserSupportFails] = useState(false);
    const [currentPlayingFromPlaylist, setCurrentPlayingFromPlaylist] = useState(null);
    const [expectingStop, setExpectingStop] = useState(false);
    const [playlistErrorMessage, setPlaylistErrorMessage] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [sendingLocalVideo, setSendingLocalVideo] = useState(true);
    const [sendingLocalAudio, setSendingLocalAudio] = useState(true);
    const [receivingRemoteVideo, setReceivingRemoteVideo] = useState(true);
    const [receivingRemoteAudio, setReceivingRemoteAudio] = useState(true);
    const [remoteConnectionSuccessful, setRemoteConnectionSuccessful] = useState(false);
    const [gettingLocalStream, setGettingLocalStream] = useState(false);
    const [showModalOpen, setShowModalOpen] = useState(false);
    const [myMediaDeviceClass, setMyMediaDeviceClass] = useState(null);
    const [callStats, setCallStats] = useState({
        jitter: 0,
        bitrateIn: 0,
        iceRoute: 'unknown',
        turnMbTotal: '0.00',
        bandwidthUsed: { mbSent: 0, mbReceived: 0, mbTotal: 0 },
    });
    const [lastPlaylist, setLastPlaylist] = useState({ volume: 99, paused: null });
    const [inputChannels, setInputChannels] = useState({ callStatus: '', registeredName: '', stream: null });
    const [iceGatherComplete, setIceGatherComplete] = useState(false);
    const [remoteTrackAdded, setRemoteTrackAdded] = useState(false);
    const [trackMuted, setTrackMuted] = useState(false);
    const [checkMute, setCheckMute] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [nonVideoRowHeight, setNonVideoRowHeight] = useState(0);
    const [panelBodyContainerHeight, setPanelBodyContainerHeight] = useState(0);
    const [remoteAspectRatio, setRemoteAspectRatio] = useState(0);
    const [hubRegistered, setHubRegistered] = useState(false);
    const [overlayPos, setOverlayPos] = useState({ x: 0, y: 0 });
    const [overlaySize, setOverlaySize] = useState({ widthRatio: 0.3 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizingOverlay, setIsResizingOverlay] = useState(false);
    const [dropdownDropup, setDropdownDropup] = useState({
        videoinput: false,
        audioinput: false,
        audiooutput: false,
        connectWith: true,
    });
    const [, setDeviceSelectionRevision] = useState(0);

    // Mutable instance refs (never trigger renders)
    const clientStreamRef = useRef({});
    const audioCtxRef = useRef({});
    const analyserRef = useRef({});
    const bufferLengthRef = useRef(0);
    const reqIdRef = useRef(0);
    const timeDataRef = useRef([]);
    const localMaxLevelRef = useRef(0);
    const localTimeMaxLevelSetRef = useRef(0);
    const webRtcControllerRef = useRef(null);
    const pendingWebrtcMessages = useRef([]);
    const clientInputGainRef = useRef(undefined);
    const timerIDRef = useRef(0);
    const muteTimerIDRef = useRef(0);
    const intervalIDRef = useRef(undefined);
    const clientStreamDestinationRef = useRef(undefined);
    const mediaStreamSourceRef = useRef(undefined);
    const masterOutputGainRef = useRef(undefined);
    const outputStreamRef = useRef(undefined);
    const parentCallStatusRef = useRef(null);
    const currentlyPlayingMediaSourceNodeRef = useRef(undefined);
    const callStatusRef = useRef('none');
    const dragStartRef = useRef({ pointerX: 0, pointerY: 0, elemX: 0, elemY: 0 });
    const resizeStartRef = useRef({ pointerX: 0, width: 0, elemX: 0, elemY: 0 });
    const localRawStreamRef = useRef(null);
    const previewStreamRef = useRef(null);
    const localOverlayMeterRef = useRef(null);
    const dropdownAnchorRefs = useRef({});
    const iosMediaDevicesUnlockedRef = useRef(false);
    const iosMediaUnlockInFlightRef = useRef(false);

    // DOM refs
    const container = useRef(null);
    const previewVideoRef = useRef(null);
    const panelBodyContainer = useRef(null);
    const nonVideoRows = useRef(null);
    const clientStreamElement = useRef(null);
    const localVideo = useRef(null);
    const remoteVideo = useRef(null);
    const fileManagerHeadings = useRef(null);
    const videoAreaRef = useRef(null);

    // State snapshot for async callbacks
    const sr = useRef({});
    sr.current = {
        callStatus, myRegisteredName, peerToConnectWith, initiator,
        gracefulCallTermination, initiatedHangUp,
        remoteConnectionSuccessful, iceGatherComplete, remoteTrackAdded,
        cameraMuted, micMuted, sendingLocalVideo, sendingLocalAudio,
        handRaised,
        containerWidth, containerHeight, maxInitLocalVideoHeight,
        currentPlayingFromPlaylist, expectingStop, myMediaDeviceClass,
        registeredNames, name, validationError, isActive,
    };

    const liveRef = useRef({ onSendMessage, parameters });
    liveRef.current.onSendMessage = onSendMessage;
    liveRef.current.parameters = parameters;

    // componentDidMount
    useEffect(() => {
        if (mode === 'setup') return;

        prefetchTurnCredentials();

        const token = keyGen.generate;
        parentCallStatusRef.current = { action: 'webcamCallStatusUpdate', instanceToken: token, callStatus: 'none' };

        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new AudioContext();
        masterOutputGainRef.current = audioCtxRef.current.createGain();
        masterOutputGainRef.current.connect(audioCtxRef.current.destination);

        const { containerHeight: ch, config } = liveRef.current.parameters;
        const storedDetails = readStoredClientDetails(config);
        if (storedDetails.name) {
            setName(storedDetails.name);
        }

        getModuleSize(ch.container, config.w, ch.maxCols);
        EventSystem.subscribe('AudioClient', processMessageStable.current);
        EventSystem.subscribe('AudioHub', processMessageStable.current);

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'register';
        liveRef.current.onSendMessage(msgObj);

        setContainerWidth(container.current.offsetWidth);
        setContainerHeight(container.current.offsetHeight);

        const cfg = liveRef.current.parameters.config;
        const deviceParams = {
            module_name: cfg.module_name,
            event_screen_module_id: cfg.event_screen_module_id,
            callType: cfg.other_config.callType,
            audioMandatory: true,
        };
        const mdc = new mediaDeviceClass(deviceParams);
        setMyMediaDeviceClass(mdc);
        liveRef.current.mdc = mdc;

        const callType = cfg.other_config.callType;
        const isIOS = isIOSBrowser();
        const constraints = callType === 'video' ? { audio: true, video: true } : { audio: true, video: false };
        if (callType !== 'video') {
            setLocalAudioHeight_fromEl();
        }

        try {
            if (isIOS) {
                setComponentMounted(true);
            } else {
                navigator.mediaDevices.getUserMedia(constraints)
                    .then(stream => {
                        // Trigger permissions and enumerate real device labels, then release immediately.
                        // getLocalStream() (called from gotDevices) will re-acquire with proper device constraints.
                        getMediaDevice();
                        stream.getTracks().forEach(t => t.stop());
                    })
                    .catch(e => {
                        alert('innit getUserMedia() error: ' + e.name);
                        console.log('error', e);
                    });
            }

            navigator.mediaDevices.ondevicechange = () => { getMediaDevice(); };
        } catch (err) {
            setBrowserSupportFails(true);
        }

        return () => {
            EventSystem.unsubscribe('AudioClient', processMessageStable.current);
            EventSystem.unsubscribe('AudioHub', processMessageStable.current);
            if (sr.current.callStatus === 'active') {
                const bye = getMessageObjectProForma();
                bye.action = 'bye';
                if (sr.current.initiator) {
                    bye.callInitiator = sr.current.myRegisteredName;
                    bye.callReceiver = sr.current.peerToConnectWith;
                } else {
                    bye.callReceiver = sr.current.myRegisteredName;
                    bye.callInitiator = sr.current.peerToConnectWith;
                }
                liveRef.current.onSendMessage(bye);
            }
            if (intervalIDRef.current !== undefined) {
                clearInterval(intervalIDRef.current);
            }
        };
    }, []);

    function setLocalAudioHeight_fromEl() {
        if (clientStreamElement.current) {
            // set local audio height; stored but not currently rendered
        }
    }

    // CWRP parameters (containerHeight / config.w)
    const firstParamsRender = useRef(true);
    useEffect(() => {
        if (firstParamsRender.current) { firstParamsRender.current = false; return; }
        getModuleSize(parameters.containerHeight.container, parameters.config.w, parameters.containerHeight.maxCols);
    }, [parameters.containerHeight.container, parameters.config.w]);

    useEffect(() => {
        if (callStatus === 'active' && videoAreaRef.current) {
            const rect = videoAreaRef.current.getBoundingClientRect();
            const ow = getOverlayWidth(rect, overlaySize.widthRatio);
            const oh = ow * 9 / 16;
            setOverlayPos({ x: rect.width - ow - 8, y: rect.height - oh - 8 });
        }
    }, [callStatus]);

    useEffect(() => {
        if (localVideo.current && localRawStreamRef.current?.getVideoTracks().length > 0) {
            localVideo.current.srcObject = localRawStreamRef.current;
        }
    }, [callStatus, sendingLocalVideo, cameraMuted]);

    // Camera preview in the device-select modal
    useEffect(() => {
        if (showModalOpen && !fileManager) {
            startPreview();
        } else {
            stopPreview();
        }
    }, [showModalOpen, fileManager]); // eslint-disable-line react-hooks/exhaustive-deps

    function getMessageObjectProForma() {
        const p = liveRef.current.parameters;
        return {
            sourceModuleName: p.config.module_name,
            event_grouping_seq: p.config.event_grouping_seq,
            customerID: p.userProfile.customer_id,
            siteID: p.userProfile.site_id,
            eventID: p.config.event_id,
        };
    }

    function getModuleSize(height, columns, maxCols) {
        setModuleWidth(Math.floor(GlobalFunction.getModuleWidth(columns, maxCols)));
        setModuleHeight(height);
    }

    function getMediaDevice() {
        return navigator.mediaDevices.enumerateDevices()
            .then(deviceInfo => {
                gotDevices(deviceInfo);
                return deviceInfo;
            })
            .catch(e => { alert('Error getting media devices:'); console.log(e); });
    }

    function gotDevices(deviceInfo) {
        console.log('enumerated devices', deviceInfo);
        const mdc = sr.current.myMediaDeviceClass || liveRef.current.mdc;
        if (!mdc) return;
        mdc.updateDevices(deviceInfo);

        if (mdc.getSelectedDevices.videoinput === '') setSendingLocalVideo(false);
        if (mdc.getSelectedDevices.audioinput === '') setSendingLocalAudio(false);

        setMyMediaDeviceClass(mdc);
        setComponentMounted(true);

        const iosWaitingForGesture = isIOSBrowser() && !iosMediaDevicesUnlockedRef.current;
        if (mdc.needsSetup() && !iosWaitingForGesture) {
            setFileManager(false);
            setShowModalOpen(true);
        }

        if (!isIOSBrowser() || sr.current.myRegisteredName.length > 0) {
            getLocalStream();
        }
    }

    function setupAudioGraph(stream) {
        // Disconnect old nodes before rebuilding to avoid double-routing
        try { mediaStreamSourceRef.current?.disconnect(); } catch (_) {}
        try { analyserRef.current?.disconnect(); } catch (_) {}
        try { clientInputGainRef.current?.disconnect(); } catch (_) {}

        // Stop old raw stream tracks to release mic/camera hardware
        if (localRawStreamRef.current) {
            localRawStreamRef.current.getTracks().forEach(t => t.stop());
        }
        localRawStreamRef.current = stream;

        stream.onmute = () => setTrackMuted(true);
        stream.mute = () => setTrackMuted(false);

        clientStreamDestinationRef.current = audioCtxRef.current.createMediaStreamDestination();
        mediaStreamSourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);

        analyserRef.current = audioCtxRef.current.createAnalyser();
        analyserRef.current.minDecibels = -100;
        analyserRef.current.maxDecibels = -30;
        timeDataRef.current = new Uint8Array(FFTSIZE);
        analyserRef.current.fftSize = FFTSIZE;
        bufferLengthRef.current = analyserRef.current.frequencyBinCount;
        localMaxLevelRef.current = 0;
        localTimeMaxLevelSetRef.current = Date.now();
        updateVerticalAudioMeter(localOverlayMeterRef, 0, 0);

        clientInputGainRef.current = audioCtxRef.current.createGain();
        clientInputGainRef.current.gain.value = 1;

        mediaStreamSourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(clientInputGainRef.current);
        clientInputGainRef.current.connect(clientStreamDestinationRef.current);

        clientStreamRef.current = clientStreamDestinationRef.current.stream;

        // Attach raw video tracks (if any) so they're sent over WebRTC
        stream.getVideoTracks().forEach(t => clientStreamRef.current.addTrack(t));

        // Show local camera preview
        if (localVideo.current && stream.getVideoTracks().length > 0) {
            localVideo.current.srcObject = stream;
        }

        // Hot-swap tracks in the active peer connection (no renegotiation needed)
        if (callStatusRef.current === 'active' && webRtcControllerRef.current) {
            webRtcControllerRef.current.replaceTracks(clientStreamRef.current.getTracks());
        }

        setGettingLocalStream(false);
    }

    function monitorVolume() {
        if (!analyserRef.current) return;

        let total = 0, i = 0, float, rms, db;
        analyserRef.current.getByteTimeDomainData(timeDataRef.current);
        while (i < bufferLengthRef.current) {
            float = (timeDataRef.current[i++] / 0x80) - 1;
            total += float * float;
        }
        rms = Math.sqrt(total / FFTSIZE);
        db = 20 * (Math.log(rms) / Math.log(10));
        db = Math.max(-48, Math.min(db, 0));

        const gainValue = (20 * Math.log10(Math.pow(10, db)) + 1000) / 1000;
        const instant = Math.max(0, Math.min(1, gainValue));

        if (instant > localMaxLevelRef.current) {
            localMaxLevelRef.current = instant;
            localTimeMaxLevelSetRef.current = Date.now();
        }
        if ((Date.now() - localTimeMaxLevelSetRef.current) > 2000) {
            localMaxLevelRef.current = instant;
            localTimeMaxLevelSetRef.current = Date.now();
        }

        updateVerticalAudioMeter(localOverlayMeterRef, instant, localMaxLevelRef.current);

        if (callStatusRef.current === 'active') {
            reqIdRef.current = requestAnimationFrame(monitorVolume);
        }
    }

    function getLocalStream() {
        const cfg = liveRef.current.parameters.config;
        let localDevice;
        if (cfg.other_config.callType === 'video') {
            localDevice = localVideo.current;
        } else {
            localDevice = clientStreamElement.current;
        }

        const mdc = sr.current.myMediaDeviceClass;
        const audioConstraint = mdc.getDeviceID('audioinput');
        const rawVideoConstraint = mdc.getDeviceID('videoinput');
        const audioOutputId = mdc.getDeviceID('audiooutput');

        if (!isIOSBrowser() && audioOutputId && localDevice.setSinkId) {
            localDevice.setSinkId(audioOutputId).catch(error => {
                console.log('setSinkId:', error);
            });
        }

        if (!audioConstraint && !rawVideoConstraint) {
            setGettingLocalStream(false);
            return;
        }

        // getDeviceID returns audio-style constraints; strip audio-only fields for video
        let videoConstraint = false;
        if (rawVideoConstraint) {
            const vid = rawVideoConstraint.deviceId?.exact;
            videoConstraint = vid ? { deviceId: { exact: vid } } : true;
        }

        const constraints = { audio: audioConstraint, video: videoConstraint };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => { setupAudioGraph(stream); })
            .catch(e => {
                alert('main getUserMedia() error: ' + e.name);
                console.log(e);
                setGettingLocalStream(false);
            });
    }

    function stopLocalDevice() {
        if (localRawStreamRef.current) {
            localRawStreamRef.current.getTracks().forEach(t => t.stop());
            localRawStreamRef.current = null;
        }
        clientStreamRef.current = null;
        if (localVideo.current) localVideo.current.srcObject = null;
    }

    function registerName() {
        const currentName = sr.current.name;
        const currentMyRegisteredName = sr.current.myRegisteredName;
        const currentCallStatus = sr.current.callStatus;

        if (currentMyRegisteredName !== currentName) {
            setMyRegisteredName(currentName);
            const mdc = sr.current.myMediaDeviceClass;
            if (mdc) {
                setSendingLocalVideo(mdc.getSelectedDevices.videoinput !== '');
                setSendingLocalAudio(mdc.getSelectedDevices.audioinput !== '');
            }
            setGettingLocalStream(true);
            getLocalStream();

            const msgObj = getMessageObjectProForma();
            msgObj.action = 'register';
            liveRef.current.onSendMessage(msgObj);

            EventSystem.publish('nameRegistered', currentName);

            writeStoredClientDetails(liveRef.current.parameters.config, { name: currentName });
        }
    }

    function sendMessage(messageObject) {
        messageObject.containerHeight = sr.current.containerHeight;
        messageObject.containerWidth = sr.current.containerWidth;
        liveRef.current.onSendMessage(messageObject);
    }

    function callFailed(channel, e) {
        console.log('Call failed in: ', e);
        stopTheCall('Connection broken');
    }

    function addChannelStream(outputStream) {
        outputStreamRef.current = outputStream;
        outputStreamRef.current.connect(masterOutputGainRef.current);
        setRemoteTrackAdded(true);
        sr.current.remoteTrackAdded = true;
        console.log('*** remoteTrackAdded');
        if (sr.current.remoteConnectionSuccessful) {
            connectionSuccessful();
        }
    }

    function addStreamToCall(channelID, stream) {
        if (typeof stream === 'undefined') {
            console.log('received stream but it is undefined so ignoring');
            return;
        }
        if (!audioCtxRef.current || !audioCtxRef.current.createMediaStreamSource) {
            console.log('received audioCTX but it is undefined so ignoring');
            return;
        }
        if (clientStreamElement.current) {
            clientStreamElement.current.srcObject = stream;
            clientStreamElement.current.muted = true;
        }
        // Show remote video if the stream has video tracks
        if (remoteVideo.current && stream.getVideoTracks().length > 0) {
            remoteVideo.current.srcObject = stream;
        }
        const streamSrc = audioCtxRef.current.createMediaStreamSource(stream);
        setInputChannels(prev => ({ ...prev, stream: streamSrc, remoteStreamReceived: true }));
        setRemoteTrackAdded(true);
        sr.current.remoteTrackAdded = true;
        console.log('*** remoteTrackAdded');
        if (sr.current.remoteConnectionSuccessful) {
            connectionSuccessful();
        }
    }

    function displayStats(channelID, reportObject) {
        setCallStats({
            jitter: parseFloat(reportObject.jitter).toFixed(3),
            bitrateIn: parseFloat(reportObject.bitrateIn).toFixed(2),
            iceRoute: reportObject.iceRoute || 'unknown',
            turnMbTotal: parseFloat(reportObject.turnMbTotal || 0).toFixed(2),
            bandwidthUsed: {
                mbSent: parseFloat(reportObject.bytesSent / 1048576).toFixed(2),
                mbReceived: parseFloat(reportObject.bytesReceived / 1048576).toFixed(2),
                mbTotal: parseFloat((reportObject.bytesSent + reportObject.bytesReceived) / 1048576).toFixed(2),
            },
        });
    }

    function setGain(messageObject) {
        if (clientInputGainRef.current) {
            clientInputGainRef.current.gain.value = (messageObject.gain + 50) / 100;
        }
    }

    function collectDebugStatus() {
        return {
            audioCtxState: audioCtxRef.current.state,
            volume: lastPlaylist.volume,
            paused: lastPlaylist.paused,
            gain: clientInputGainRef.current ? clientInputGainRef.current.gain.value : 0,
        };
    }

    function sendDebug(messageObject) {
        messageObject.messageOriginator = sr.current.myRegisteredName;
        messageObject.action = 'debugResponse';
        if (messageObject.command === 'suspend') {
            audioCtxRef.current.suspend().then(() => {
                messageObject.debug = collectDebugStatus();
                liveRef.current.onSendMessage(messageObject);
            });
        } else if (messageObject.command === 'resume') {
            audioCtxRef.current.resume().then(() => {
                messageObject.debug = collectDebugStatus();
                liveRef.current.onSendMessage(messageObject);
            });
        } else {
            messageObject.debug = collectDebugStatus();
            liveRef.current.onSendMessage(messageObject);
        }
    }

    function nameRegisterStart() {
        setRegisteredNames([]);
        if (sr.current.myRegisteredName.length > 0) {
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'instanceRegisteredName';
            const mdc = sr.current.myMediaDeviceClass;
            msgObj.audioinput = mdc ? mdc.getSelectedDevices.audioinput : '';
            msgObj.registeredName = sr.current.myRegisteredName;
            msgObj.containerHeight = container.current ? container.current.offsetHeight : 0;
            msgObj.containerWidth = container.current ? container.current.offsetWidth : 0;
            msgObj.callStatus = 'none';
            liveRef.current.onSendMessage(msgObj);
        }
    }

    function handleInboundCall(messageObject) {
        console.log('handleInboundCall', messageObject);
    }

    function processMessageImpl(messageObject) {
        if (liveRef.current.parameters.config.event_grouping_seq !== messageObject.event_grouping_seq) return;

        if (messageObject.action === 'debugRequest') {
            sendDebug(messageObject);
        } else if (messageObject.action === 'register') {
            nameRegisterStart();
        } else if (messageObject.action === 'setGain' &&
                   messageObject.callInitiator === sr.current.myRegisteredName) {
            setGain(messageObject);
        } else if (messageObject.action === 'channelStateChange' &&
                   messageObject.callInitiator === sr.current.myRegisteredName) {
            if (messageObject.channelOn) {
                setIsActive(true);
            } else {
                if (intervalIDRef.current !== undefined) clearInterval(intervalIDRef.current);
                setIsActive(false);
            }
        } else if (messageObject.action === 'callingRequest' &&
                   messageObject.callReceiver === sr.current.myRegisteredName) {
            handleInboundCall(messageObject);
        } else if (messageObject.action === 'callingResponse' &&
                   messageObject.callInitiator === sr.current.myRegisteredName) {
            if (messageObject.response === 'busy') {
                callStatusRef.current = 'none';
                setCallStatus('none');
                if (parentCallStatusRef.current) {
                    parentCallStatusRef.current.callStatus = 'none';
                    liveRef.current.onSendMessage(parentCallStatusRef.current);
                }
                setTerminationMessage('Busy');
                timerIDRef.current = setTimeout(() => setTerminationMessage(''), 4000);
            } else {
                getTurnCredentials().then(pcConfig => {
                    const controllerParams = {
                        messageObject,
                        channelID: 0,
                        myRegisteredName: sr.current.myRegisteredName,
                        callType: 'video',
                        localStream: clientStreamRef.current,
                        sendingLocalVideo: clientStreamRef.current?.getVideoTracks().length > 0,
                        sendingLocalAudio: sr.current.sendingLocalAudio,
                        pcConfig,
                    };
                    const ctrl = new webRtcController(controllerParams);
                    ctrl.on('sendMessage', sendMessage);
                    ctrl.on('connectionSuccessful', webRtcConnected);
                    ctrl.on('callFailed', callFailed);
                    ctrl.on('replaceTracks', callFailed);
                    ctrl.on('remoteStreamObtained', addStreamToCall);
                    ctrl.on('stats', displayStats);
                    ctrl.on('sendStateDebugInfo', processWebRTCDebugEvent);
                    const audioCfg = liveRef.current.parameters.config.other_config;
                    if (audioCfg.audio_codec && audioCfg.audio_codec.length > 0) {
                        ctrl.audio_codec = audioCfg.audio_codec;
                    }
                    webRtcControllerRef.current = ctrl;
                    pendingWebrtcMessages.current.forEach(msg => ctrl.handshakeReceived(msg));
                    pendingWebrtcMessages.current = [];
                });
            }
        } else if (messageObject.action === 'instanceRegisteredName') {
            if (messageObject.registeredName !== sr.current.myRegisteredName) {
                const nameStructure = {
                    registeredName: messageObject.registeredName,
                    containerHeight: messageObject.containerHeight,
                    containerWidth: messageObject.containerWidth,
                    sourceModuleName: messageObject.sourceModuleName,
                    callStatus: messageObject.callStatus,
                };
                const existingIndex = sr.current.registeredNames.findIndex(r => r.registeredName === messageObject.registeredName);
                const newNames = [...sr.current.registeredNames];
                if (existingIndex === -1) {
                    newNames.push(nameStructure);
                } else {
                    newNames[existingIndex] = { ...newNames[existingIndex], ...nameStructure };
                }
                setRegisteredNames(newNames);

                const hubNames = newNames.filter(n => n.sourceModuleName === 'AudioHub');
                if (hubNames.length === 1) {
                    setPeerToConnectWith(hubNames[0].registeredName);
                    setRemoteAspectRatio(hubNames[0].containerWidth / hubNames[0].containerHeight);
                } else {
                    setPeerToConnectWith('');
                    setRemoteAspectRatio(0);
                }

                if (newNames.findIndex(r => r.registeredName === sr.current.name) === -1) {
                    setValidationError(null);
                } else {
                    setValidationError('error');
                }
            }
        } else if (messageObject.action === 'peerDisconnected') {
            // Hub (or any peer) disconnected abruptly — if it was our call partner, end the call
            if (messageObject.registeredName === sr.current.peerToConnectWith &&
                (sr.current.callStatus === 'active' || sr.current.callStatus === 'calling')) {
                callStatusRef.current = 'bye';
                setCallStatus('bye');
                stopTheCall(messageObject.registeredName + ' disconnected');
            }
            // Remove from registered names list regardless
            setRegisteredNames(prev => prev.filter(r => r.registeredName !== messageObject.registeredName));
        } else if (messageObject.action === 'bye' &&
                   (messageObject.callReceiver === sr.current.myRegisteredName ||
                    messageObject.callInitiator === sr.current.myRegisteredName)) {
            setGracefulCallTermination(true);
            callStatusRef.current = 'bye';
            setCallStatus('bye');
            stopTheCall(messageObject.callInitiator + ' hung up the call');
        } else if (messageObject.action === 'audioMute' &&
                   messageObject.callReceiver === sr.current.myRegisteredName) {
            setRemoteAudioMuted(messageObject.micMuted);
        } else if (messageObject.action === 'raiseHand' &&
                   messageObject.callReceiver === sr.current.myRegisteredName &&
                   messageObject.callInitiator === sr.current.peerToConnectWith) {
            setHandRaised(messageObject.handRaised);
        } else if (messageObject.action === 'remoteConnectionSuccess') {
            if (messageObject.callInitiator === sr.current.myRegisteredName ||
                messageObject.callInitiator === sr.current.peerToConnectWith) {
                setRemoteConnectionSuccessful(true);
                sr.current.remoteConnectionSuccessful = true;
                console.log('*** remoteConnectionSuccessful');
                if (sr.current.remoteTrackAdded) {
                    connectionSuccessful();
                }
            }
        } else if (messageObject.action === 'webrtc') {
            if (sr.current.callStatus !== 'bye') {
                if (messageObject.callInitiator === sr.current.myRegisteredName ||
                    messageObject.callReceiver === sr.current.myRegisteredName) {
                    if (webRtcControllerRef.current) {
                        webRtcControllerRef.current.handshakeReceived(messageObject);
                    } else {
                        pendingWebrtcMessages.current.push(messageObject);
                    }
                }
            }
        }
    }

    const processMessageStable = useRef(function processMessageWrapper(msg) {
        processMessageImpl(msg);
    });

    function webRtcConnected() {
        console.log('we believe the call is working, waiting to hear from the hub');
        setIceGatherComplete(true);
        sr.current.iceGatherComplete = true;
        console.log('*** iceGatherComplete');
        if (sr.current.remoteConnectionSuccessful && sr.current.remoteTrackAdded) {
            console.log('hub already said it is successful');
            connectionSuccessful();
        }
    }

    function connectionSuccessful() {
        if (!sr.current.remoteTrackAdded || !sr.current.remoteConnectionSuccessful) {
            console.log('ATTENTION: Error - someone called connectionSuccessful too early');
            return;
        }
        if (sr.current.callStatus === 'active') {
            console.log('double dipping');
            return;
        }

        setGracefulCallTermination(false);
        setHideRemoteVideo(false);
        callStatusRef.current = 'active';
        setCallStatus('active');
        reqIdRef.current = requestAnimationFrame(monitorVolume);

        if (parentCallStatusRef.current) {
            parentCallStatusRef.current.callStatus = 'active';
            liveRef.current.onSendMessage(parentCallStatusRef.current);
        }

        if (sr.current.initiator) {
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'register';
            liveRef.current.onSendMessage(msgObj);
        }

        const toneMsg = getMessageObjectProForma();
        toneMsg.callInitiator = sr.current.myRegisteredName;
        toneMsg.client = 'AudioClient';
        toneMsg.action = 'requestConnectedTone';
        liveRef.current.onSendMessage(toneMsg);
    }

    function stopTheCall(stopReason) {
        console.log('stopping the call -', stopReason);
        if (webRtcControllerRef.current !== null) {
            webRtcControllerRef.current.close();
            webRtcControllerRef.current = null;
        }
        pendingWebrtcMessages.current = [];

        callStatusRef.current = 'none';
        setCallStatus('none');

        if (parentCallStatusRef.current) {
            parentCallStatusRef.current.callStatus = 'none';
            liveRef.current.onSendMessage(parentCallStatusRef.current);
        }

        if (intervalIDRef.current !== undefined) clearInterval(intervalIDRef.current);

        if (sr.current.cameraMuted) unMuteCamera();
        if (sr.current.micMuted) unMuteMic();

        setIsActive(false);
        setInitiator(false);
        setCameraMuted(false);
        setMicMuted(false);
        setRemoteAudioMuted(false);
        setRemoteCameraMuted(false);
        setHandRaised(false);
        setHideRemoteVideo(true);
        setRemoteConnectionSuccessful(false);
        setIceGatherComplete(false);
        setRemoteTrackAdded(false);
        setInputChannels({ callStatus: '', registeredName: '', stream: null });
        if (remoteVideo.current) remoteVideo.current.srcObject = null;
        if (clientStreamElement.current) clientStreamElement.current.srcObject = null;

        if (!sr.current.initiatedHangUp) {
            if (sr.current.gracefulCallTermination) {
                setTerminationMessage('Remote end hung up');
            } else {
                setTerminationMessage(stopReason);
            }
            timerIDRef.current = setTimeout(() => setTerminationMessage(''), 4000);
        }
        setInitiatedHangUp(false);
        setGracefulCallTermination(false);

        if (liveRef.current.parameters.config.other_config.callType === 'video' && panelBodyContainer.current) {
            const availableWidth = panelBodyContainer.current.offsetWidth;
            const dims = calcImageDimensions(availableWidth, sr.current.maxInitLocalVideoHeight, sr.current.remoteAspectRatio);
            // localVideoWidth/Height are derived in render, not state
        }
    }

    function processWebRTCDebugEvent(channelID, debugObject) {
        EventSystem.publish('webRTCdebug', { registeredName: sr.current.myRegisteredName, debugObject });
        const msgObj = getMessageObjectProForma();
        msgObj.sourceModuleName = 'remoteWebRTCdebug';
        msgObj.callReceiver = sr.current.myRegisteredName;
        msgObj.callInitiator = sr.current.peerToConnectWith;
        msgObj.client = 'AudioClient';
        msgObj.debugObject = debugObject;
        liveRef.current.onSendMessage(msgObj);
    }

    function hangupCall() {
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'bye';
        if (sr.current.initiator) {
            msgObj.callInitiator = sr.current.myRegisteredName;
            msgObj.callReceiver = sr.current.peerToConnectWith;
        } else {
            msgObj.callReceiver = sr.current.myRegisteredName;
            msgObj.callInitiator = sr.current.peerToConnectWith;
        }
        liveRef.current.onSendMessage(msgObj);
        setInitiatedHangUp(true);
    }

    function muteCam() {
        const newMuted = !sr.current.cameraMuted;
        setCameraMuted(newMuted);
        if (clientStreamRef.current && clientStreamRef.current.getVideoTracks) {
            clientStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !newMuted; });
        }
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'cameraMute';
        msgObj.callInitiator = sr.current.myRegisteredName;
        msgObj.callReceiver = sr.current.peerToConnectWith;
        msgObj.cameraMuted = newMuted;
        liveRef.current.onSendMessage(msgObj);
    }

    function unMuteCamera() {
        if (clientStreamRef.current && clientStreamRef.current.getVideoTracks) {
            clientStreamRef.current.getVideoTracks().forEach(t => { t.enabled = true; });
        }
    }

    function muteMic() {
        muteTimerIDRef.current = setTimeout(() => {
            const newMuted = !sr.current.micMuted;
            setMicMuted(newMuted);
            if (clientStreamRef.current && clientStreamRef.current.getAudioTracks) {
                clientStreamRef.current.getAudioTracks().forEach(t => {
                    t.enabled = !newMuted;
                    setCheckMute(t.muted);
                });
            }
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'audioMute';
            msgObj.callInitiator = sr.current.myRegisteredName;
            msgObj.callReceiver = sr.current.peerToConnectWith;
            msgObj.micMuted = newMuted;
            liveRef.current.onSendMessage(msgObj);
        }, 100);
    }

    function unMuteMic() {
        if (clientStreamRef.current && clientStreamRef.current.getAudioTracks) {
            clientStreamRef.current.getAudioTracks().forEach(t => { t.enabled = true; });
        }
    }

    function toggleRaiseHand() {
        const nextHandRaised = !sr.current.handRaised;
        setHandRaised(nextHandRaised);

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'raiseHand';
        msgObj.callInitiator = sr.current.myRegisteredName;
        msgObj.callReceiver = sr.current.peerToConnectWith;
        msgObj.handRaised = nextHandRaised;
        liveRef.current.onSendMessage(msgObj);
    }

    function playerStatusChanged(newState, mediaSourceNode, audioObject) {
        setLastPlaylist({ volume: audioObject.volume, paused: audioObject.paused });
        if (sr.current.callStatus === 'none') return;

        if (newState === 'start') {
            if (sr.current.currentPlayingFromPlaylist !== null) {
                setExpectingStop(true);
                sr.current.currentPlayingFromPlaylist.pause();
                if (currentlyPlayingMediaSourceNodeRef.current) {
                    currentlyPlayingMediaSourceNodeRef.current.disconnect(clientInputGainRef.current);
                }
                currentlyPlayingMediaSourceNodeRef.current = mediaSourceNode;
                setCurrentPlayingFromPlaylist(audioObject);
            } else {
                currentlyPlayingMediaSourceNodeRef.current = mediaSourceNode;
                setCurrentPlayingFromPlaylist(audioObject);
            }
            mediaSourceNode.connect(clientStreamDestinationRef.current);
            if (clientInputGainRef.current) clientInputGainRef.current.gain.value = 0;
        } else if (newState === 'stop' && sr.current.expectingStop) {
            setExpectingStop(false);
        } else if (newState === 'stop') {
            if (clientInputGainRef.current) clientInputGainRef.current.gain.value = 1;
            if (currentlyPlayingMediaSourceNodeRef.current) {
                currentlyPlayingMediaSourceNodeRef.current.disconnect(clientInputGainRef.current);
            }
            setCurrentPlayingFromPlaylist(null);
        }
    }

    function removePlayer(key) {
        setAudioListItems(prev => {
            const index = prev.findIndex(item => item.key === key);
            if (index === -1) { alert('Cannot find %s in array to remove', key); return prev; }
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    }

    function selectAudioFile(e) {
        if (typeof e.target.files[0] === 'undefined') return;
        const file = e.target.files[0];
        setAudioListItems(prev => {
            if (prev.findIndex(item => item.key === file.name) !== -1) {
                setPlaylistErrorMessage('File with that name already loaded');
                timerIDRef.current = setTimeout(() => setPlaylistErrorMessage(''), 3000);
                return prev;
            }
            return [...prev, (
                <ListGroupItem key={file.name}>
                    <MyAudioPlayer sourceFile={file} audioCtx={audioCtxRef.current} onStatusChange={playerStatusChanged} onRemovePlayer={removePlayer} />
                </ListGroupItem>
            )];
        });
    }

    function handleChange(e) {
        if (e.target.name === 'registerName') {
            const val = e.target.value;
            if (sr.current.registeredNames.findIndex(r => r.registeredName === val) === -1) {
                setValidationError(null);
            } else {
                setValidationError('error');
            }
            setName(val);
            writeStoredClientDetails(liveRef.current.parameters.config, { name: val });
        } else if (e.target.name === 'unlockField') {
            const val = e.target.value;
            if (val.length === 3) {
                setShowModalOpen(false);
            }
        }
    }

    function handleKeyPress(e) {
        if (e.key === 'Enter' && sr.current.validationError === null && e.target.value.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            registerName();
        }
    }

    function buttonClick(e) {
        const msgObj = getMessageObjectProForma();
        if (e.target.name === 'register') {
            registerName();
        } else if (e.target.name === 'connect') {
            if (sr.current.callStatus === 'none') {
                setInitiator(true);
                if (audioCtxRef.current.resume) audioCtxRef.current.resume();
                msgObj.callInitiator = sr.current.myRegisteredName;
                msgObj.callReceiver = sr.current.peerToConnectWith;
                msgObj.action = 'callingRequest';
                callStatusRef.current = 'calling';
                setCallStatus('calling');
                if (parentCallStatusRef.current) {
                    parentCallStatusRef.current.callStatus = 'calling';
                    liveRef.current.onSendMessage(parentCallStatusRef.current);
                }
            } else {
                msgObj.action = 'bye';
                if (sr.current.initiator) {
                    msgObj.callInitiator = sr.current.myRegisteredName;
                    msgObj.callReceiver = sr.current.peerToConnectWith;
                } else {
                    msgObj.callReceiver = sr.current.myRegisteredName;
                    msgObj.callInitiator = sr.current.peerToConnectWith;
                }
            }
            liveRef.current.onSendMessage(msgObj);
        } else if (e.target.name === 'audioFile') {
            setFileManager(true);
            setShowModalOpen(true);
        }
    }

    function connectPeerSelected(eventKey) {
        setPeerToConnectWith(eventKey);
        const idx = sr.current.registeredNames.findIndex(r => r.registeredName === eventKey);
        if (idx !== -1) {
            const r = sr.current.registeredNames[idx];
            setRemoteAspectRatio(r.containerWidth / r.containerHeight);
        }
    }

    function getDeviceOption(kind, deviceInfo) {
        if (deviceInfo.kind === kind) {
            return <option key={`${kind}-${deviceInfo.deviceId || deviceInfo.label}`} value={deviceInfo.label}>{deviceInfo.label}</option>;
        }
        return null;
    }

    function createMenuItem(nameObj) {
        if (registeredNames.length > 0 && nameObj.sourceModuleName === 'AudioHub') {
            return <MenuItem key={nameObj.registeredName} eventKey={nameObj.registeredName} disabled={nameObj.callStatus === 'active'}>{nameObj.registeredName}</MenuItem>;
        }
        return null;
    }

    function selectVideoInput(eventKey) {
        const mdc = sr.current.myMediaDeviceClass;
        if (!mdc) return;

        const sel = { ...mdc.getSelectedDevices, videoinput: eventKey === 'None' ? '' : eventKey };
        mdc.selectedDevices = sel;
        setMyMediaDeviceClass(mdc);
        setDeviceSelectionRevision(prev => prev + 1);
        startPreview(mdc);

        if (sr.current.myRegisteredName.length > 0) {
            stopLocalDevice();
            getLocalStream();
        }
    }

    function selectAudioInput(eventKey) {
        const mdc = sr.current.myMediaDeviceClass;
        if (!mdc) return;

        const sel = { ...mdc.getSelectedDevices, audioinput: eventKey === 'None' ? '' : eventKey };
        mdc.selectedDevices = sel;
        setMyMediaDeviceClass(mdc);
        setDeviceSelectionRevision(prev => prev + 1);

        if (sr.current.myRegisteredName.length > 0) {
            stopLocalDevice();
            getLocalStream();
        }
    }

    function selectAudioOutput(eventKey) {
        if (isIOSBrowser()) return;

        setMyMediaDeviceClass(prev => {
            const mdc = prev;
            const sel = mdc.getSelectedDevices;
            sel.audiooutput = eventKey;
            mdc.selectedDevices = sel;
            return mdc;
        });
        // Apply immediately to remote playback elements
        [clientStreamElement.current, remoteVideo.current].forEach(el => {
            if (el && el.setSinkId) el.setSinkId(eventKey).catch(e => console.log('setSinkId:', e));
        });
    }

    function calcImageDimensions(availableWidth, availableHeight, aspectRatio) {
        const neededWidth = Math.floor(availableHeight * aspectRatio);
        if (neededWidth < availableWidth) {
            return { screenWidth: neededWidth, screenHeight: availableHeight };
        }
        return { screenWidth: availableWidth, screenHeight: Math.floor(availableWidth / aspectRatio) };
    }

    function stopPreview() {
        if (previewStreamRef.current) {
            previewStreamRef.current.getTracks().forEach(t => t.stop());
            previewStreamRef.current = null;
        }
        if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
    }

    function startPreview(mdc) {
        stopPreview();
        const source = mdc || sr.current.myMediaDeviceClass;
        if (!source) return;
        const rawVideoConstraint = source.getDeviceID('videoinput');
        if (!rawVideoConstraint) return;
        const vid = rawVideoConstraint.deviceId?.exact;
        const videoConstraint = vid ? { deviceId: { exact: vid } } : true;
        navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: false })
            .then(stream => {
                previewStreamRef.current = stream;
                if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;
            })
            .catch(() => {});
    }

    function unlockIOSMediaDevicesForSelection() {
        if (!isIOSBrowser()) return;
        if (iosMediaUnlockInFlightRef.current) return;
        iosMediaUnlockInFlightRef.current = true;

        const cfg = liveRef.current.parameters.config;
        const constraints = cfg.other_config.callType === 'video'
            ? { audio: true, video: true }
            : { audio: true, video: false };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                iosMediaDevicesUnlockedRef.current = true;
                stream.getTracks().forEach(t => t.stop());
                return getMediaDevice();
            })
            .then(() => {
                startPreview();
            })
            .catch(e => {
                console.log('iOS media selection unlock failed:', e);
            })
            .finally(() => {
                iosMediaUnlockInFlightRef.current = false;
            });
    }

    function openModal(e) {
        setShowModalOpen(true);
        setFileManager(false);
        unlockIOSMediaDevicesForSelection();
    }

    function closeModal() {
        stopPreview();
        setShowModalOpen(false);
        setFileManager(false);
    }

    function displayDebug() {
        EventSystem.publish('webRTCdebugState', true);
    }

    function getOverlayWidth(rect, widthRatio) {
        const minWidth = Math.min(140, rect.width);
        const maxWidthByHeight = rect.height * 16 / 9;
        const maxWidth = Math.max(minWidth, Math.min(rect.width * 0.75, maxWidthByHeight));
        const requestedWidth = rect.width * widthRatio;
        return Math.max(minWidth, Math.min(maxWidth, requestedWidth));
    }

    function handleOverlayPointerDown(e) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStartRef.current = { pointerX: e.clientX, pointerY: e.clientY, elemX: overlayPos.x, elemY: overlayPos.y };
        setIsDragging(true);
    }

    function handleOverlayResizePointerDown(e) {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const rect = videoAreaRef.current?.getBoundingClientRect();
        if (!rect) return;
        resizeStartRef.current = {
            pointerX: e.clientX,
            width: getOverlayWidth(rect, overlaySize.widthRatio),
            elemX: overlayPos.x,
            elemY: overlayPos.y,
        };
        setIsResizingOverlay(true);
    }

    function handleOverlayPointerMove(e) {
        if ((!isDragging && !isResizingOverlay) || !videoAreaRef.current) return;
        const rect = videoAreaRef.current.getBoundingClientRect();
        if (isResizingOverlay) {
            const nextWidth = getOverlayWidth(rect, (resizeStartRef.current.width + e.clientX - resizeStartRef.current.pointerX) / rect.width);
            const nextHeight = nextWidth * 9 / 16;
            const nextX = Math.max(0, Math.min(rect.width - nextWidth, resizeStartRef.current.elemX));
            const nextY = Math.max(0, Math.min(rect.height - nextHeight, resizeStartRef.current.elemY));
            setOverlaySize({ widthRatio: nextWidth / rect.width });
            setOverlayPos({ x: nextX, y: nextY });
            return;
        }
        const ow = getOverlayWidth(rect, overlaySize.widthRatio);
        const oh = ow * 9 / 16;
        const newX = Math.max(0, Math.min(rect.width - ow, dragStartRef.current.elemX + e.clientX - dragStartRef.current.pointerX));
        const newY = Math.max(0, Math.min(rect.height - oh, dragStartRef.current.elemY + e.clientY - dragStartRef.current.pointerY));
        setOverlayPos({ x: newX, y: newY });
    }

    function handleOverlayPointerUp() {
        setIsDragging(false);
        setIsResizingOverlay(false);
    }

    function setDropdownAnchor(name, el) {
        if (el) dropdownAnchorRefs.current[name] = el;
    }

    function updateDropdownDirection(name, anchorEl) {
        const anchor = anchorEl || dropdownAnchorRefs.current[name];
        const bounds = container.current || document.documentElement;
        if (!anchor || !bounds?.getBoundingClientRect) return;

        const anchorRect = anchor.getBoundingClientRect();
        const boundsRect = bounds.getBoundingClientRect();
        const estimatedMenuHeight = Math.min(260, Math.max(150, localMediaDevices.length * 28 + 12));
        const spaceAbove = anchorRect.top - boundsRect.top;
        const spaceBelow = boundsRect.bottom - anchorRect.bottom;
        const shouldDropUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

        setDropdownDropup(prev => (
            prev[name] === shouldDropUp ? prev : { ...prev, [name]: shouldDropUp }
        ));
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    const cfg = parameters.config;

    let defaultAudioOutput = '';
    let defaultAudioInput = '';
    let defaultVideoInput = '';
    let localMediaDevices = [{ kind: '' }];
    if (myMediaDeviceClass !== null) {
        defaultAudioOutput = myMediaDeviceClass.getSelectedDevices.audiooutput;
        defaultAudioInput = myMediaDeviceClass.getSelectedDevices.audioinput === '' ? 'None' : myMediaDeviceClass.getSelectedDevices.audioinput;
        defaultVideoInput = myMediaDeviceClass.getSelectedDevices.videoinput === '' ? 'None' : myMediaDeviceClass.getSelectedDevices.videoinput;
        localMediaDevices = myMediaDeviceClass.getLocalMediaDevices;
    }

    const rowStyle = { height: parameters.containerHeight.container + 'px', borderStyle: 'none' };
    const borderColours = { warning: '#eea236', primary: '#2e6da4', danger: '#d43f3a', success: '#4cae4c', default: '#ccc', info: '#1b6d85' };
    const borderColor = borderColours[cfg.colour];
    const webcamHeading = cfg.description && cfg.description.length > 0 ? cfg.description : 'Webcam';
    const hubList = registeredNames.filter(n => n.sourceModuleName === 'AudioHub');
    const audioFileInputId = 'audioFile' + cfg.event_grouping_seq;
    const dataUsage = 'Data: ' + callStats.bandwidthUsed.mbTotal + 'MB';
    const bitrateIn = callStats.bitrateIn + 'k';
    const iceRouteLabel = callStats.iceRoute === 'turn' ? 'TURN' : callStats.iceRoute === 'direct' ? 'Direct' : 'Route unknown';
    const isVideoCall = cfg.other_config.callType === 'video';
    const isIOSDevice = isIOSBrowser();
    const supportsAudioOutputSelection = !isIOSDevice;
    const stackDeviceSelectors = isIOSDevice;
    let deviceSelectColumns = stackDeviceSelectors ? 12 : 4;
    if (!stackDeviceSelectors && containerWidth < 300) deviceSelectColumns = 12;
    else if (!stackDeviceSelectors && containerWidth < 500) deviceSelectColumns = 6;
    const previewVideoStyle = {
        width: '100%',
        maxHeight: stackDeviceSelectors ? 'none' : '160px',
        aspectRatio: '16/9',
        objectFit: 'contain',
        borderRadius: 6,
        backgroundColor: '#111',
        display: 'block',
    };
    const rootClassName = stackDeviceSelectors ? 'ac-ios-narrow' : '';

    return (
        <div className={rootClassName} style={{ height: '100%', width: '100%' }} ref={container}>
            {/* DEVICE / PLAYLIST MODAL */}
            <Modal show={showModalOpen} autoFocus>

                <Modal.Header>
                    <Modal.Title>
                        <Row style={{ height: 40 }}>
                            <Col xs={9} style={{ paddingTop: '0px' }}>
                                {fileManager ? 'File Manager' : 'Select Devices'}
                            </Col>
                            <Col xs={3} style={{ paddingTop: '0px', display: fileManager && callStatus !== 'active' ? 'block' : 'none' }}>
                                <input type="file" id={audioFileInputId} name="audioFile"
                                    style={{ opacity: 0, position: 'absolute', pointerEvents: 'none', width: '1px', height: '1px' }}
                                    accept=".mp3"
                                    onChange={selectAudioFile} />
                                <label htmlFor={audioFileInputId} style={{ float: 'right' }}>
                                    <span className="btn btn-warning">Add Audio</span>
                                </label>
                            </Col>
                        </Row>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Camera preview — only for video call type */}
                    {cfg.other_config.callType === 'video' && (
                        <Row style={{ display: fileManager ? 'none' : 'block', marginBottom: 8 }}>
                            <Col xs={12} style={{ textAlign: 'center' }}>
                                <video ref={previewVideoRef} autoPlay muted playsInline
                                    style={previewVideoStyle} />
                            </Col>
                        </Row>
                    )}
                    <Row style={{ display: fileManager ? 'none' : 'block' }}>
                        <Col xs={deviceSelectColumns}
                            style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px' }}>
                            <img src="staticImages/webcam-transparent.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2 }} />
                            <FormControl componentClass="select" value={defaultVideoInput}
                                style={{ width: '100%', height: '34px', overflow: 'hidden', textOverflow: 'ellipsis',
                                         left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left' }}
                                onChange={e => selectVideoInput(e.target.value)}>
                                {_.map(localMediaDevices, device => getDeviceOption('videoinput', device))}
                            </FormControl>
                        </Col>
                        <Col xs={deviceSelectColumns}
                            style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px' }}>
                            <img src="staticImages/mic-on.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2 }} />
                            <FormControl componentClass="select" value={defaultAudioInput}
                                style={{ width: '100%', height: '34px', overflow: 'hidden', textOverflow: 'ellipsis',
                                         left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left' }}
                                onChange={e => selectAudioInput(e.target.value)}>
                                {_.map(localMediaDevices, device => getDeviceOption('audioinput', device))}
                            </FormControl>
                        </Col>
                        <Col xs={deviceSelectColumns}
                            style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px',
                                     display: supportsAudioOutputSelection ? 'block' : 'none' }}>
                            <img src="staticImages/speaker-on.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2,
                                         display: defaultAudioOutput.length === 0 ? 'none' : 'block' }} />
                            <FormControl componentClass="select" value={defaultAudioOutput}
                                style={{ width: '100%', height: '34px', overflow: 'hidden', textOverflow: 'ellipsis',
                                         left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left',
                                         display: defaultAudioOutput.length === 0 ? 'none' : 'block' }}
                                onChange={e => selectAudioOutput(e.target.value)}>
                                {_.map(localMediaDevices, device => getDeviceOption('audiooutput', device))}
                            </FormControl>
                        </Col>
                    </Row>
                    <Row style={{ display: fileManager ? 'block' : 'none' }}>
                        <Col xs={7} style={{ paddingTop: '5px', color: 'red', fontSize: '16pt' }}>
                            {playlistErrorMessage}
                        </Col>
                    </Row>
                    <Row style={{ display: fileManager ? 'block' : 'none' }}>
                        <Col xs={12}>
                            <ListGroup style={{ width: '100%', overflowY: 'scroll',
                                               borderColor, borderWidth: '1px',
                                               borderStyle: audioListItems.length === 0 ? 'none' : 'solid',
                                               backgroundColor: audioListItems.length === 0 ? '' : 'white' }}>
                                {audioListItems}
                            </ListGroup>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* MAIN PANEL */}
            <Tile className="flex-col" style={rowStyle}>
                <Title style={{ borderStyle: 'none', height: '38px', display: 'flex', alignItems: 'center', padding: '0 8px 0 30px' }}>
                    <span style={{ whiteSpace: 'nowrap', marginRight: '12px' }}>{webcamHeading}</span>
                    <div onClick={displayDebug}
                        style={{ position: 'absolute', right: 8, top: 0, cursor: 'pointer', padding: '5px' }}>
                        <i style={{ fontSize: '28px' }} className="material-icons">bug_report</i>
                    </div>
                </Title>
                <Body className="flex-grow globalBackground"
                    style={{ padding: 0, overflow: 'hidden' }}>
                    <div ref={panelBodyContainer}
                        style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                        {/* ── IDENTITY STRIP ───────────────────────────────── */}
                        <div className="ac-identity-strip">
                            {myRegisteredName === '' ? (
                                <form className="ac-name-form"
                                    onSubmit={e => { e.preventDefault(); registerName(); }}>
                                    <FormControl type="text" placeholder="Your name" name="registerName"
                                        autoFocus value={name}
                                        onKeyPress={handleKeyPress}
                                        onChange={handleChange}
                                        className="ac-name-input-inline" />
                                    <Button type="submit" bsSize="small" className="ac-message-btn"
                                        disabled={name.trim().length === 0 || validationError !== null}>
                                        Register
                                    </Button>
                                </form>
                            ) : (
                                <>
                                    <span className="ac-dot ac-dot-green" />
                                    <span className="ac-identity-name">{myRegisteredName}</span>
                                </>
                            )}
                            <Glyphicon glyph="cog" className="cog"
                                onTouchStart={openModal} onClick={openModal}
                                style={{ fontSize: 22, marginLeft: 'auto', flexShrink: 0, cursor: 'pointer', padding: '4px' }} />
                        </div>

                        {/* ── VIDEO ZONE ───────────────────────────────────── */}
                        <div className="ac-video-zone" ref={videoAreaRef}
                            onPointerMove={handleOverlayPointerMove}
                            onPointerUp={handleOverlayPointerUp}>

                            {/* Hidden audio element — always in DOM for WebRTC */}
                            <audio autoPlay ref={clientStreamElement} style={{ display: 'none' }} />

                            {/* Remote video */}
                            <video ref={remoteVideo} autoPlay playsInline
                                style={{ position: 'absolute', top: '50%', left: '50%',
                                         transform: 'translate(-50%, -50%)',
                                         width: '100%', height: 'auto', maxHeight: '100%',
                                         aspectRatio: '16/9', backgroundColor: '#000',
                                         objectFit: 'contain',
                                         display: callStatus === 'active' && !hideRemoteVideo ? 'block' : 'none' }} />

                            {/* Local camera — full zone when idle, draggable/resizable PiP when active */}
                            {callStatus === 'active' ? (
                                <div
                                    onPointerDown={handleOverlayPointerDown}
                                    style={{
                                        position: 'absolute',
                                        left: overlayPos.x + 'px',
                                        top: overlayPos.y + 'px',
                                        width: (overlaySize.widthRatio * 100) + '%',
                                        aspectRatio: '16/9',
                                        zIndex: 3,
                                        borderRadius: 4,
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        display: sendingLocalVideo && !cameraMuted ? 'block' : 'none',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
                                        backgroundColor: '#000',
                                        overflow: 'hidden',
                                    }}>
                                    <video ref={localVideo} autoPlay muted playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                    <div
                                        onPointerDown={handleOverlayResizePointerDown}
                                        style={{
                                            position: 'absolute',
                                            right: 0,
                                            bottom: 0,
                                            width: 18,
                                            height: 18,
                                            cursor: 'nwse-resize',
                                            background: 'linear-gradient(135deg, transparent 45%, rgba(255,255,255,0.65) 46%, rgba(255,255,255,0.65) 55%, transparent 56%)',
                                            zIndex: 4,
                                        }} />
                                </div>
                            ) : (
                                <video ref={localVideo} autoPlay muted playsInline
                                    style={{
                                        position: 'absolute', top: '50%', left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '100%', height: 'auto', maxHeight: '100%',
                                        aspectRatio: '16/9', backgroundColor: '#000',
                                        objectFit: 'contain',
                                        display: sendingLocalVideo ? 'block' : 'none',
                                    }} />
                            )}

                            {/* No-camera fallback */}
                            {!sendingLocalVideo && callStatus !== 'active' && (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                              alignItems: 'center', justifyContent: 'center',
                                              flexDirection: 'column', gap: 8 }}>
                                    <img src="staticImages/hollow-mic-on.svg" height="40" width="40"
                                        style={{ opacity: 0.2 }} />
                                    {myRegisteredName !== '' && (
                                        <span style={{ color: '#555', fontSize: '13px' }}>{myRegisteredName}</span>
                                    )}
                                </div>
                            )}

                            {/* Calling overlay */}
                            {callStatus === 'calling' && (
                                <div className="ac-calling-overlay">
                                    <span className="ac-pulse-ring" />
                                    <span>Calling {peerToConnectWith}…</span>
                                </div>
                            )}

                            {/* Active-call overlays */}
                            {callStatus === 'active' && (
                                <>
                                    <span style={{ color: 'black', backgroundColor: 'rgba(255,255,255,0.8)',
                                                   fontSize: '14px', padding: '2px 8px', borderRadius: 4,
                                                   display: hideRemoteVideo ? 'none' : 'block',
                                                   position: 'absolute', top: 10, left: 10, zIndex: 4 }}>
                                        {peerToConnectWith}
                                    </span>
                                    <span style={{ color: 'white', fontSize: '13px', position: 'absolute',
                                                   top: 34, left: 10, zIndex: 4,
                                                   display: remoteCameraMuted || !receivingRemoteVideo ? 'block' : 'none' }}>
                                        {remoteCameraMuted ? 'Camera muted' : 'No video'}
                                    </span>
                                    <span style={{ color: 'white', fontSize: '13px', position: 'absolute',
                                                   top: 54, left: 10, zIndex: 4,
                                                   display: remoteAudioMuted ? 'block' : 'none' }}>
                                        Audio muted
                                    </span>
                                    <img src={cameraMuted ? 'staticImages/webcam-off.svg' : 'staticImages/webcam-on.svg'}
                                        height="24" width="24" className="cog" onClick={muteCam}
                                        style={{ zIndex: 5, position: 'absolute', top: 8, right: 8, borderRadius: '20px',
                                                 display: sendingLocalVideo ? 'inline-block' : 'none' }} />
                                    <img src={micMuted ? 'staticImages/mic-off.svg' : 'staticImages/mic-on.svg'}
                                        height="24" width="24" className="cog" onClick={muteMic}
                                        style={{ zIndex: 5, position: 'absolute', top: 36, right: 8, borderRadius: '20px',
                                                 display: sendingLocalAudio ? 'inline-block' : 'none' }} />
                                    <div style={{ zIndex: 5, position: 'absolute', top: 37, right: 34,
                                                  display: sendingLocalAudio ? 'block' : 'none' }}>
                                        <VerticalAudioMeter meterRef={localOverlayMeterRef} height={20} />
                                    </div>
                                </>
                            )}

                            {/* Playlist button — subtle corner overlay when idle and registered */}
                            {myRegisteredName !== '' && callStatus === 'none' && (
                                <button className="ac-playlist-btn" name="audioFile" onClick={buttonClick}>
                                    <img src="staticImages/audioFile.svg" height="13" width="13" />
                                    Playlist
                                </button>
                            )}

                            {/* Termination message */}
                            {terminationMessage.length > 0 && (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex',
                                              alignItems: 'center', justifyContent: 'center', zIndex: 6 }}>
                                    <span style={{ color: 'white', fontSize: '14pt',
                                                   backgroundColor: 'rgba(60,60,60,0.9)',
                                                   padding: '5px 16px', borderRadius: '20px' }}>
                                        {terminationMessage}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* ── ACTION BAR ───────────────────────────────────── */}
                        <div className={`ac-action-bar${callStatus === 'active' && isActive ? ' live' : ''}`}>

                            {/* Pre-join */}
                            {myRegisteredName === '' && (
                                <span className="ac-action-hint">Enter your name above to get started</span>
                            )}

                            {/* Idle — hub selector + call */}
                            {myRegisteredName !== '' && callStatus === 'none' && (
                                <>
                                    <span ref={el => setDropdownAnchor('connectWith', el)}
                                        onMouseDownCapture={e => updateDropdownDirection('connectWith', e.currentTarget)}
                                        onTouchStartCapture={e => updateDropdownDirection('connectWith', e.currentTarget)}>
                                    <DropdownButton
                                        dropup={dropdownDropup.connectWith}
                                        title={peerToConnectWith.length > 0 ? peerToConnectWith : 'Select hub'}
                                        id={'connectWith' + cfg.event_grouping_seq}
                                        bsSize="small"
                                        className="ac-hub-select"
                                        onToggle={isOpen => { if (isOpen) updateDropdownDirection('connectWith'); }}
                                        onSelect={connectPeerSelected}
                                        disabled={hubList.length === 0}>
                                        {_.map(hubList, n => (
                                            <MenuItem key={n.registeredName} eventKey={n.registeredName}
                                                disabled={n.callStatus === 'active'}>
                                                {n.registeredName}
                                            </MenuItem>
                                        ))}
                                    </DropdownButton>
                                    </span>
                                    <Button bsSize="small" name="connect" className="ac-message-btn"
                                        disabled={peerToConnectWith.length === 0}
                                        onClick={buttonClick}>
                                        Call
                                    </Button>
                                    {terminationMessage.length > 0 && (
                                        <span className="ac-termination-msg">{terminationMessage}</span>
                                    )}
                                </>
                            )}

                            {/* Calling */}
                            {callStatus === 'calling' && (
                                <>
                                    <span className="ac-pulse-ring" style={{ flexShrink: 0 }} />
                                    <span className="ac-action-hint">Calling {peerToConnectWith}…</span>
                                    <Button bsSize="small" name="connect" onClick={buttonClick}
                                        className="ac-message-btn"
                                        style={{ marginLeft: 'auto', flexShrink: 0 }}>
                                        Cancel
                                    </Button>
                                </>
                            )}

                            {/* Active */}
                            {callStatus === 'active' && (
                                <>
                                    <span className={`ac-live-pill${isActive ? ' live' : ' on-hold'}`}>
                                        {isActive ? 'ON AIR' : 'ON HOLD'}
                                    </span>
                                    <div className="ac-analyser-wrap" style={{ flex: 1, minWidth: 0 }}>
                                        <MiniAnalyser stream={inputChannels.stream}
                                            audioContext={audioCtxRef.current}
                                            audioStreamAvailable={addChannelStream} />
                                    </div>
                                    <span className="ac-quality-dot" style={{ flexShrink: 0,
                                        backgroundColor: callStats.jitter < 0.01 ? '#2ecc71'
                                                        : callStats.jitter < 0.03 ? '#f39c12' : '#e74c3c' }}
                                        title={`${bitrateIn} · ${dataUsage} · ${iceRouteLabel} · TURN ${callStats.turnMbTotal}MB · Jitter ${callStats.jitter}`} />
                                    <button className={`ac-ctrl-btn ac-raise-hand${handRaised ? ' active' : ''}`}
                                        onClick={toggleRaiseHand}
                                        style={{ flexShrink: 0 }}>
                                        {handRaised ? 'Lower Hand' : 'Raise Hand'}
                                    </button>
                                    <button className="ac-ctrl-btn ac-btn-hangup" onClick={hangupCall}
                                        style={{ flexShrink: 0 }}>
                                        <img src="staticImages/hangup.svg" height="18" width="18" />
                                        End Call
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Browser not supported */}
                        {browserSupportFails && (
                            <div style={{ position: 'absolute', inset: 0, zIndex: 1000,
                                          backgroundColor: 'blue', color: 'yellow',
                                          fontSize: '26pt', padding: '10px' }}>
                                Your browser does not support WebRTC — module disabled
                            </div>
                        )}
                    </div>
                </Body>
            </Tile>
        </div>
    );
}
